type Card = { id?: string; title: string; description: string }
type Step = { index: number; title: string; description: string }
type LinkItem = { route: string; anchor: string }
type FaqItem = { question: string; answer: string }
type AudienceItem = { label: string; body: string }

type PageContent = {
  page: string
  meta: { title: string; description: string; h1: string; ogTitle: string; ogDescription: string }
  sections: {
    hero: {
      eyebrow: string
      h1: string
      subtitle: string
      primaryCta: string
      secondaryCta: string
    }
    capabilities: { h2: string; cards: Card[] }
    workflow: { h2: string; steps: Step[] }
    useCases: { h2: string; items: Card[] }
    audience?: { h2: string; items: AudienceItem[] }
    benefits: { h2: string; items: Card[] }
    limits: { h2: string; body: string }
    related: { h2: string; links: LinkItem[] }
    faq: { h2: string; items: FaqItem[] }
    finalCta: { h2: string; description: string; primaryCta: string }
  }
}

type PageEntry = {
  key: string
  route: string
  content: PageContent
  images: Array<{
    assetId: string
    section: string
    action: string
    purpose: string
    src: string | null
  }>
}

function ProductCta({
  children,
  className = 'button button-primary',
}: {
  children: string
  className?: string
}) {
  return (
    <a className={className} href="/go/">
      {children}
    </a>
  )
}

function ProductProof({ title, steps }: { title: string; steps: Step[] }) {
  return (
    <div className="seo-product-ui" aria-label={`Демонстрация сценария: ${title}`}>
      <div className="seo-ui-toolbar">
        <span>Редактор</span>
        <i />
        <i />
        <i />
        <b>Готово</b>
      </div>
      <div className="seo-ui-body">
        <div className="seo-ui-rail">
          {steps.map((step) => (
            <span key={step.index}>{String(step.index).padStart(2, '0')}</span>
          ))}
        </div>
        <div className="seo-ui-canvas">
          <small>Рабочая область</small>
          <strong>{title}</strong>
          <div className="seo-ui-art">
            <i />
            <i />
            <i />
          </div>
        </div>
      </div>
    </div>
  )
}

export default function SeoPage({ page }: { page: PageEntry }) {
  const { sections } = page.content
  const heroImage = page.images.find((image) => image.section === 'hero' && image.src)
  const exampleImage = page.images.find((image) => image.section === 'examples' && image.src)

  return (
    <main className="seo-page" id="main-content">
      <section className="seo-hero section" id="top">
        <div className="container seo-hero-grid">
          <div className="seo-hero-copy">
            <nav className="breadcrumbs" aria-label="Хлебные крошки">
              <a href="/">Главная</a>
              <span>/</span>
              <span>{sections.hero.eyebrow}</span>
            </nav>
            <p className="eyebrow">{sections.hero.eyebrow}</p>
            <h1>{sections.hero.h1}</h1>
            <p className="hero-lead">{sections.hero.subtitle}</p>
            <div className="button-row">
              <ProductCta className="button button-primary button-lg">
                Попробовать бесплатно
              </ProductCta>
            </div>
          </div>
          {heroImage && (
            <figure className="seo-hero-media">
              <img
                src={heroImage.src!}
                width="1536"
                height="1024"
                alt={heroImage.purpose}
                fetchPriority="high"
              />
            </figure>
          )}
        </div>
      </section>

      <section className="section seo-capabilities" id="capabilities">
        <div className="container">
          <div className="section-heading narrow">
            <h2>{sections.capabilities.h2}</h2>
          </div>
          <div className="seo-card-grid">
            {sections.capabilities.cards.map((card, index) => (
              <article
                className={`seo-card seo-card-${(index % 3) + 1}`}
                key={card.id ?? card.title}
              >
                <span>{String(index + 1).padStart(2, '0')}</span>
                <div className="seo-card-art" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </div>
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section seo-workflow" id="experience">
        <div className="container seo-workflow-grid">
          <div>
            <p className="eyebrow">Понятный процесс</p>
            <h2>{sections.workflow.h2}</h2>
            <ol className="seo-step-list">
              {sections.workflow.steps.map((step) => (
                <li key={step.index}>
                  <span>{String(step.index).padStart(2, '0')}</span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <ProductProof title={sections.workflow.h2} steps={sections.workflow.steps} />
        </div>
      </section>

      <section className="section seo-usecases" id="showcase">
        <div className="container seo-usecases-grid">
          {exampleImage && (
            <figure className="seo-example-media">
              <img
                src={exampleImage.src!}
                width="1536"
                height="1024"
                alt={exampleImage.purpose}
                loading="lazy"
                decoding="async"
              />
            </figure>
          )}
          <div>
            <div className="section-heading">
              <h2>{sections.useCases.h2}</h2>
            </div>
            <div className="seo-usecase-list">
              {sections.useCases.items.map((item) => (
                <article key={item.title}>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {sections.audience && (
        <section className="section seo-audience">
          <div className="container seo-audience-grid">
            <div className="section-heading">
              <h2>{sections.audience.h2}</h2>
              <p>Сверьте задачу, исходную ситуацию и способ работы до перехода к инструменту.</p>
            </div>
            <div className="seo-audience-list">
              {sections.audience.items.map((item) => (
                <article key={item.label}>
                  <span>{item.label}</span>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section seo-benefits">
        <div className="container">
          <div className="section-heading narrow">
            <h2>{sections.benefits.h2}</h2>
          </div>
          <div className="seo-benefit-grid">
            {sections.benefits.items.map((item, index) => (
              <article key={item.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <div className="seo-benefit-art" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section seo-limit">
        <div className="container disclosure-panel">
          <div>
            <span className="card-kicker">Важно</span>
            <h2>{sections.limits.h2}</h2>
          </div>
          <p>{sections.limits.body}</p>
        </div>
      </section>

      <section className="section seo-related">
        <div className="container">
          <div className="section-heading narrow">
            <h2>{sections.related.h2}</h2>
          </div>
          <div className="related-links">
            {sections.related.links.map((link) => (
              <a href={link.route} key={link.route}>
                {link.anchor}
                <span aria-hidden="true">→</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="section faq-section" id="faq">
        <div className="container faq-grid">
          <div className="section-heading">
            <h2>{sections.faq.h2}</h2>
            <p>Ответы по выбранному сценарию и важные ограничения.</p>
          </div>
          <div className="faq-list">
            {sections.faq.items.map((item, index) => (
              <details open={index === 0} key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="final-section">
        <div className="container final-panel">
          <div className="final-copy">
            <h2>{sections.finalCta.h2}</h2>
            <p>{sections.finalCta.description}</p>
            <ProductCta className="button button-primary button-lg">
              Попробовать бесплатно
            </ProductCta>
          </div>
          <div className="final-orbit" aria-hidden="true">
            <span className="orbit-card orbit-one" />
            <span className="orbit-card orbit-two" />
            <span className="orbit-card orbit-three" />
          </div>
        </div>
      </section>
    </main>
  )
}
